import type { GridRequestInput } from "~/gql/graphqlBase.generated";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

export const RECIPE_TARGET_KIND = "recipe";

export const LINKING_RECIPES_TARGET_KIND = "linkingRecipes";

export const RECIPES_BASE_PATH = "/produce/production/recipes";

export const RECIPES_DOCK_PANEL_ID = "recipes";

const RESERVED_RECIPE_SEGMENTS = ["import", "link-health"];

const RECIPE_ROUTE_PATTERN = /^\/produce\/production\/recipes\/([^/?#]+)$/;

const LINKING_RECIPE_IDS_FILTER = "productionRecipeIds";

const LINKING_RECIPE_ID_SEPARATOR = ",";

export const matchRecipeReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = RECIPE_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	const targetId = decodeURIComponent(match[1]);
	if (RESERVED_RECIPE_SEGMENTS.includes(targetId)) return null;
	return { targetId, targetKind: RECIPE_TARGET_KIND };
};

export const isRecipesRoute = (pathname: string) =>
	pathname === RECIPES_BASE_PATH || matchRecipeReceiverRoute(pathname) !== null;

export const buildLinkingRecipesTarget = (
	productionItemId: string,
	productionRecipeIds: readonly string[]
): ContinuationTarget => ({
	listFilters: { [LINKING_RECIPE_IDS_FILTER]: productionRecipeIds.join(LINKING_RECIPE_ID_SEPARATOR) },
	targetId: productionItemId,
	targetKind: LINKING_RECIPES_TARGET_KIND
});

export const linkingRecipeIds = (listFilters: Record<string, string> | undefined): string[] =>
	(listFilters?.[LINKING_RECIPE_IDS_FILTER] ?? "").split(LINKING_RECIPE_ID_SEPARATOR).filter(Boolean);

export const buildLinkingRecipesFilterModel = (productionRecipeIds: readonly string[]) => ({
	productionRecipeId: { filterType: "set", values: [...productionRecipeIds] }
});

export const withLinkingRecipesFilter = (
	request: GridRequestInput,
	productionRecipeIds: readonly string[] | null
): GridRequestInput =>
	productionRecipeIds
		? {
				...request,
				filterModel: { ...(request.filterModel as object), ...buildLinkingRecipesFilterModel(productionRecipeIds) }
			}
		: request;

export const linkingRecipesReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: () => RECIPES_BASE_PATH,
	matchesRoute: () => null
};

const recipeReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${RECIPES_BASE_PATH}/${encodeURIComponent(targetId)}`,
	matchesRoute: matchRecipeReceiverRoute
};

export default recipeReceiverIdentityAdapter;
